import { useCallback, useRef, useState } from 'react';
import {
    saveScoreHidden,
    saveScoreOverride,
    SCORE_OVERRIDE_ERROR_MESSAGES,
    SCORE_VISIBILITY_ERROR_MESSAGES,
} from '../../services/matchingApi.js';
import { isScoreHidden } from './scoreVisibility';
import { readScoreOverride, withScoreOverride } from './scoreOverride';

/**
 * マッチ度の調整 (表示・非表示の切り替え IN-M-009 と、数字の書き換え IN-M-010) の状態と保存。
 * 画面レイアウト_SCR-M-001.md §6.2「マッチ度の表示・非表示の切り替え」「マッチ度の数字の書き換え」、Issue #1435。
 *
 * useAiMatching から呼ぶ (useAiMatching.js を大きくしないために分けた)。
 *
 * 保存中・編集中・失敗の文言は、どの推薦結果に対するものかを一緒に持つ。保存の間に表示する結果が
 * 替わったとき (保存中に実行した等)、前の結果の状態を新しい結果に出さないため。
 * 保存は重ねて送らない (表示の設定の保存と数字の保存のどちらかが済むまで、次を受け付けない)。
 *
 * @param {object|null} matchingResult 表示中の推薦結果
 * @param {Function} setMatchingResult
 * @param {Function} setOrderedItems 画面の並び (カードに渡す要素) の更新
 * 保存中かは ref で持ち、押した時点の値で判定する (描画の時点の状態で判定すると、保存を始めてから
 * 再描画されるまでの間に押された操作を止められない。Issue #992)。
 *
 * @param {{current: boolean}|null} [otherSavingRef] 求職者に提案するか (Issue #992)・おすすめポイントの文
 *   (Issue #777) のどちらかの保存中か (useAiMatching が savingRefs.js でまとめて渡す)。その間は
 *   マッチ度の保存を呼ばない (同じ推薦結果の行をロックして書くため。SCR-M-001 §6.2「同時に保存するもの」)
 * @param {{current: boolean}|null} [savingRef] 本フックの保存中を書き込む ref。求職者に提案するかのフックが
 *   押した時点で読む。渡さなければ本フックの中だけで持つ
 */
function useScoreAdjustments(
    matchingResult, setMatchingResult, setOrderedItems, otherSavingRef = null, savingRef = null
) {
    const shownResultId = matchingResult?.id ?? null;
    // 保存の応答を受け取った時点で表示中の結果を知るために持つ (保存を始めた時点の値ではなく)。
    // 画面の並び (orderedItems) は結果の ID を持たないため、別の結果に替わった後に、同じ求人 ID の
    // 要素へ前の結果の値を入れないよう、これで確かめる
    const shownResultIdRef = useRef(shownResultId);
    shownResultIdRef.current = shownResultId;
    // 保存済みの表示の設定は表示中の結果から読む (再読込・キャッシュを使った再実行でも同じ読み方になる)
    const scoreHidden = isScoreHidden(matchingResult);

    // 表示の設定: 保存中の、切り替えた後の設定 ({ resultId, hidden }) と、失敗の文言 ({ resultId, message })
    const [scoreHiddenPending, setScoreHiddenPending] = useState(null);
    const [scoreVisibilityFailure, setScoreVisibilityFailure] = useState(null);
    // 数字: 編集中の求人 ({ resultId, itemId })・保存中の求人 (同)・失敗の文言 ({ resultId, itemId, message })
    const [scoreEditing, setScoreEditing] = useState(null);
    const [scoreOverrideSaving, setScoreOverrideSaving] = useState(null);
    const [scoreOverrideFailure, setScoreOverrideFailure] = useState(null);

    const forShown = (state) => (state && state.resultId === shownResultId ? state : null);
    const pendingForShown = forShown(scoreHiddenPending);
    const visibilityFailureForShown = forShown(scoreVisibilityFailure);
    const editingForShown = forShown(scoreEditing);
    const overrideSavingForShown = forShown(scoreOverrideSaving);
    const overrideFailureForShown = forShown(scoreOverrideFailure);

    // どの結果の保存でも真 (保存を重ねて送らない)。求職者に提案するかの保存中も含める (Issue #992)。
    // 押した時点の値で判定するため ref を読む (描画の時点の状態は再描画まで古い)
    const ownRef = useRef(false);
    const ownSavingRef = savingRef ?? ownRef;

    // 切り替えた時点で保存する (保存のボタンは設けない。並べ替えのように「画面では変えたのに
    // PDF には出ない」状態を作らないため)。カードの表示は応答を受け取ってから切り替える
    const changeScoreHidden = useCallback(
        async (nextHidden) => {
            if (!matchingResult || ownSavingRef.current || otherSavingRef?.current || editingForShown) return;
            const targetId = matchingResult.id;
            ownSavingRef.current = true;
            setScoreHiddenPending({ resultId: targetId, hidden: nextHidden });
            setScoreVisibilityFailure(null);

            // 保存中の印は finally で戻す (戻らないと、以後すべての保存が押せなくなり、エラーとしても見えない)
            try {
                let result;
                try {
                    result = await saveScoreHidden(targetId, nextHidden);
                } catch (err) {
                    // 失敗は saveScoreHidden が結果で返す。ここに来るのは想定外の例外だけ。
                    // 保存中のまま止めない (切り替えも PDF 出力も押せなくなるため)
                    console.error('useScoreAdjustments: マッチ度の表示の設定の保存で例外が発生しました', err);
                    result = { ok: false, errorMessage: SCORE_VISIBILITY_ERROR_MESSAGES.network };
                }

                if (result.ok) {
                    // 設定は応答から読み直す。並び (orderedItems) は応答で置き換えない
                    // (保存していない並べ替えが戻ってしまうため)。保存の間に別の結果へ替わっていたら入れない
                    setMatchingResult((prev) =>
                        prev && prev.id === targetId
                            ? { ...prev, score_hidden: result.data.score_hidden }
                            : prev
                    );
                } else {
                    setScoreVisibilityFailure({ resultId: targetId, message: result.errorMessage });
                }
            } finally {
                ownSavingRef.current = false;
                setScoreHiddenPending(null);
            }
        },
        [matchingResult, ownSavingRef, otherSavingRef, editingForShown, setMatchingResult]
    );

    // 編集を始める (BTN-M-010)。編集できるカードは同時に 1 枚だけ。非表示の間は編集できない
    const startScoreEdit = useCallback(
        (jobPoolItemId) => {
            if (!matchingResult || ownSavingRef.current || otherSavingRef?.current || editingForShown || scoreHidden) {
                return;
            }
            setScoreEditing({ resultId: matchingResult.id, itemId: jobPoolItemId });
            setScoreOverrideFailure(null);
        },
        [matchingResult, ownSavingRef, otherSavingRef, editingForShown, scoreHidden]
    );

    // 編集をやめる (BTN-M-012。編集中に実行したときも呼ぶ)。保存中はやめられない。
    // 編集中でなければ何もしない (「AI の値に戻す」の失敗の文言を、実行で消さないため)
    const cancelScoreEdit = useCallback(() => {
        if (ownSavingRef.current || otherSavingRef?.current || scoreEditing === null) return;
        setScoreEditing(null);
        setScoreOverrideFailure(null);
    }, [ownSavingRef, otherSavingRef, scoreEditing]);

    const clearScoreOverrideError = useCallback(() => setScoreOverrideFailure(null), []);

    // 求人 1 件のマッチ度を保存する (BTN-M-011)。score が null なら手で直した値を消す (BTN-M-013)。
    // 保存できたら true。画面の値は応答の値で入れ替える (送った値をそのまま使わない)
    const saveScore = useCallback(
        async (jobPoolItemId, score) => {
            if (!matchingResult || ownSavingRef.current || otherSavingRef?.current) return false;
            // 別のカードを編集している間は、ほかのカードの保存 (AI の値に戻す) を受け付けない
            if (editingForShown && editingForShown.itemId !== jobPoolItemId) return false;
            const targetId = matchingResult.id;
            ownSavingRef.current = true;
            setScoreOverrideSaving({ resultId: targetId, itemId: jobPoolItemId });
            setScoreOverrideFailure(null);

            // 保存中の印は finally で戻す (changeScoreHidden と同じ)
            try {
                let result;
                try {
                    result = await saveScoreOverride(targetId, jobPoolItemId, score);
                } catch (err) {
                    console.error('useScoreAdjustments: マッチ度の保存で例外が発生しました', err);
                    result = { ok: false, errorMessage: SCORE_OVERRIDE_ERROR_MESSAGES.network };
                }

                if (result.ok) {
                    const savedOverride = readScoreOverride(result.recommendation);
                    const applySaved = (rec) =>
                        rec.job_pool_item_id === jobPoolItemId ? withScoreOverride(rec, savedOverride) : rec;
                    // 保存の間に別の結果へ替わっていたら入れない。並びは変えない (その求人の値だけを入れ替える)
                    setMatchingResult((prev) =>
                        prev && prev.id === targetId && Array.isArray(prev.recommendations)
                            ? { ...prev, recommendations: prev.recommendations.map(applySaved) }
                            : prev
                    );
                    if (shownResultIdRef.current === targetId) {
                        setOrderedItems((prev) => prev.map(applySaved));
                    }
                    setScoreEditing((prev) =>
                        prev && prev.resultId === targetId && prev.itemId === jobPoolItemId ? null : prev
                    );
                } else {
                    setScoreOverrideFailure({ resultId: targetId, itemId: jobPoolItemId, message: result.errorMessage });
                }
                return result.ok;
            } finally {
                ownSavingRef.current = false;
                setScoreOverrideSaving(null);
            }
        },
        [matchingResult, ownSavingRef, otherSavingRef, editingForShown, setMatchingResult, setOrderedItems]
    );

    return {
        // 表示の設定 (IN-M-009)。scoreHidden は保存済みの設定 (カードの表示に使う)、
        // scoreVisibleChecked はチェックボックスの表示 (保存中は切り替えた後の状態)
        scoreHidden,
        scoreVisibleChecked: !(pendingForShown ? pendingForShown.hidden : scoreHidden),
        scoreVisibilitySaving: scoreHiddenPending !== null,
        scoreVisibilityError: visibilityFailureForShown ? visibilityFailureForShown.message : null,
        changeScoreHidden,
        // 数字の書き換え (IN-M-010)。scoreOverrideSaving は、どの結果の保存でも真 (scoreVisibilitySaving と同じ)。
        // savingScoreItemId は、表示中の結果で保存中の求人 (そのカードに「保存中...」を出す)
        scoreOverrideSaving: scoreOverrideSaving !== null,
        editingScoreItemId: editingForShown ? editingForShown.itemId : null,
        savingScoreItemId: overrideSavingForShown ? overrideSavingForShown.itemId : null,
        scoreOverrideError: overrideFailureForShown
            ? { itemId: overrideFailureForShown.itemId, message: overrideFailureForShown.message }
            : null,
        startScoreEdit,
        cancelScoreEdit,
        clearScoreOverrideError,
        saveScore,
    };
}

export default useScoreAdjustments;
